'use client';

import { useCallback, useMemo } from 'react';
import { useLocale, useTranslations } from 'next-intl';

import { Button } from '@/components/ui/Button';
import { RepairStatusStepper } from '@/components/service/RepairStatusStepper';
import { useRouter } from '@/lib/i18n/navigation';
import { BRAND_NAME } from '@/lib/const';
import type { RepairDetails, RepairReasonResponse } from '@/lib/service/ecs/contracts';
import { getRepairLogisticsCapabilities } from '@/lib/service/ecs/logisticsCapabilities';
import { formatRepairReasons } from '@/lib/service/formatRepairReasons';
import {
  buildRepairStatusTimeline,
  getRepairTimelineActiveStepIndex,
} from '@/lib/service/repairDetailsTimeline';
import { formatOrderDate } from '@/lib/orders/formatOrderDate';
import { getCountryFromLocale } from '@/lib/i18n';
import type {
  BuildRepairUpdateRequestInput,
  RepairDetailsGuestAccess,
} from '@/lib/service/repairDetailsUpdate';
import { buildServiceQuotePath } from '@/lib/service/serviceQuoteQuery';
import { RepairRequestReasonSummary } from '@/features/service/repair-request/RepairRequestReasonSummary';
import { RepairRequestServiceCentre } from '@/features/service/repair-request/RepairRequestServiceCentre';
import { RepairRequestShippingKitSelector } from '@/features/service/repair-request/RepairRequestShippingKitSelector';

import { RepairDetailsAddressSection } from './RepairDetailsAddressSection';
import { RepairDetailsContactSection } from './RepairDetailsContactSection';
import { RepairDetailsReadOnlySection } from './RepairDetailsReadOnlySection';
import { RepairDetailsReturnMethodSection } from './RepairDetailsReturnMethodSection';
import styles from './RepairDetailsRepairPanel.module.scss';

interface RepairDetailsRepairPanelProps {
  repair: RepairDetails;
  reasonCatalogue: RepairReasonResponse['items'] | undefined;
  isUpdating: boolean;
  returnContext?: string;
  guestAccess?: RepairDetailsGuestAccess;
  onUpdate: (input: Omit<BuildRepairUpdateRequestInput, 'repair'>) => Promise<void>;
}

export function RepairDetailsRepairPanel({
  repair,
  reasonCatalogue,
  isUpdating,
  returnContext,
  guestAccess,
  onUpdate,
}: Readonly<RepairDetailsRepairPanelProps>) {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations('service.repairDetails');
  const tStatus = useTranslations('service.repairDetails.status');
  const tPersonal = useTranslations('service.repairRequest.personal');
  const tReview = useTranslations('service.repairRequest.review');
  const countryCode = getCountryFromLocale(locale).toUpperCase();
  const capabilities = useMemo(() => getRepairLogisticsCapabilities(countryCode), [countryCode]);
  const canChange = repair.deliveryOptionEditable;
  const contactStartsInEditMode = !repair.shippingAddress.phoneNumber;

  const timeline = buildRepairStatusTimeline(repair);
  const activeStepIndex = getRepairTimelineActiveStepIndex(timeline);
  const timelineSteps = timeline.map((step) => ({
    label: tStatus(step.actionCode),
    dateLabel:
      step.status === 'Completed' && step.dateTime
        ? formatOrderDate(step.dateTime, locale)
        : undefined,
  }));

  const reasons = formatRepairReasons(repair.repairItems[0]?.reasonCodes ?? [], reasonCatalogue);
  const remarks = repair.repairItems[0]?.remarks ?? '';

  const submitUpdate = useCallback(
    (input: Omit<BuildRepairUpdateRequestInput, 'repair' | 'guestAccess'>) =>
      onUpdate({ ...input, guestAccess }),
    [guestAccess, onUpdate]
  );

  const handleQuoteNavigation = useCallback(() => {
    if (!repair.quoteId || !returnContext) return;
    router.push(buildServiceQuotePath(repair.repairNumber, repair.quoteId, returnContext));
  }, [repair.quoteId, repair.repairNumber, returnContext, router]);

  const quoteCtaDisabled = isUpdating || !repair.shippingAddress.phoneNumber;

  return (
    <div className={styles.panel} data-testid="RepairDetailsRepairPanel">
      <div className={styles.metaGrid}>
        <div className={styles.metaBlock}>
          <span className={styles.metaLabel}>{t('serviceCreateDate')}</span>
          <span className={styles.metaValue}>
            {repair.creationDateTime ? formatOrderDate(repair.creationDateTime, locale) : '—'}
          </span>
        </div>
        <div className={styles.metaBlock}>
          <span className={styles.metaLabel}>{t('repairNo')}</span>
          <span className={styles.metaValue}>{repair.repairNumber}</span>
        </div>
        <div className={styles.metaBlock}>
          <span className={styles.metaLabel}>{t('serviceBoutique')}</span>
          <span className={styles.metaValue}>{repair.boutiqueName || '—'}</span>
        </div>
      </div>

      {repair.plannedDeliveryDate ? (
        <p className={styles.plannedDelivery} data-testid="repair-details-planned-delivery">
          <span>{t('plannedDeliveryDate')}</span>
          <strong>{formatOrderDate(repair.plannedDeliveryDate, locale)}</strong>
        </p>
      ) : null}

      <RepairStatusStepper steps={timelineSteps} activeIndex={activeStepIndex} />

      {repair.quoteId ? (
        <div className={styles.quoteCtaRow}>
          <Button
            type="button"
            variant="primary"
            onClick={handleQuoteNavigation}
            disabled={quoteCtaDisabled}
            data-testid="repair-details-quote-cta"
          >
            {t('quoteAvailable')}
          </Button>
        </div>
      ) : null}

      <RepairDetailsReadOnlySection title={t('serviceDetails')} testId="repair-details-reasons">
        <RepairRequestReasonSummary reasons={reasons} reasonComment={remarks} />
      </RepairDetailsReadOnlySection>

      <RepairDetailsReadOnlySection
        title={t('deliverToBrand', { brand: BRAND_NAME })}
        testId="repair-details-delivery"
      >
        {repair.modeOfTransport === 'BoutiqueDropoff' ? (
          <p className={styles.boutiqueName}>{repair.dropoffBoutiqueName || '—'}</p>
        ) : (
          <>
            <RepairRequestShippingKitSelector
              selected={
                repair.modeOfTransport === 'BreitlingSending' ? 'BreitlingSending' : 'ClientSending'
              }
              showBrandKit={capabilities.hasBrandShippingKit}
              readOnly
            />
            <RepairRequestServiceCentre
              address={capabilities.csPlatformAddress}
              className={styles.serviceCentre}
            />
          </>
        )}
      </RepairDetailsReadOnlySection>

      <RepairDetailsReturnMethodSection
        repair={repair}
        canChange={canChange}
        isProcessing={isUpdating}
        onSubmit={(returnMethod) => submitUpdate({ kind: 'returnMethod', returnMethod })}
      />

      <RepairDetailsAddressSection
        title={tReview('addresses.shippingTitle')}
        testId="repair-details-shipping"
        address={repair.shippingAddress}
        canChange={canChange}
        isProcessing={isUpdating}
        onSubmit={(address) => submitUpdate({ kind: 'shippingAddress', address })}
      />

      <RepairDetailsAddressSection
        title={tPersonal('address.billingTitle')}
        testId="repair-details-billing"
        address={repair.billingAddress}
        canChange={canChange}
        isProcessing={isUpdating}
        onSubmit={(address) => submitUpdate({ kind: 'billingAddress', address })}
      />

      <RepairDetailsContactSection
        shippingAddress={repair.shippingAddress}
        canChange={canChange}
        startInEditMode={contactStartsInEditMode}
        isProcessing={isUpdating}
        onSubmit={(contact) => submitUpdate({ kind: 'contactInfo', contact })}
      />
    </div>
  );
}
